import { initializePaddle, type Environments, type Paddle } from '@paddle/paddle-js'
import { logCheckout, maskSecret } from './subscribeCheckoutDebug'

let paddleInitPromise: Promise<Paddle | undefined> | null = null
let initEnvironment: Environments | null = null

function clientToken(): string | undefined {
  const t = import.meta.env.VITE_PADDLE_CLIENT_TOKEN
  return typeof t === 'string' && t.trim().length > 0 ? t.trim() : undefined
}

/** True when webapp can open checkout via Paddle.js (like Paddle’s CodePen examples). */
export function isPaddleJsConfigured(): boolean {
  return !!clientToken()
}

/**
 * Initialize Paddle.js once (sandbox vs production must match server / catalog).
 */
export function ensurePaddleJs(environment: Environments): Promise<Paddle | undefined> {
  const token = clientToken()
  if (!token) {
    logCheckout('paddleJs.ensurePaddleJs.skip', {
      reason: 'no VITE_PADDLE_CLIENT_TOKEN in bundle (set at npm run build before S3 deploy)',
      requestedEnvironment: environment,
    })
    return Promise.resolve(undefined)
  }

  if (!paddleInitPromise || initEnvironment !== environment) {
    initEnvironment = environment
    logCheckout('paddleJs.initializePaddle.start', {
      environment,
      tokenPreview: maskSecret(token),
    })
    paddleInitPromise = initializePaddle({
      token,
      environment,
    }).then(
      (instance) => {
        logCheckout('paddleJs.initializePaddle.ok', {
          environment,
          hasCheckout: !!instance?.Checkout,
        })
        return instance
      },
      (err: unknown) => {
        logCheckout('paddleJs.initializePaddle.failed', {
          environment,
          error: err instanceof Error ? err.message : String(err),
        })
        throw err
      },
    )
  }

  return paddleInitPromise
}

/** Overlay checkout — same pattern as https://codepen.io/heymcgovern/pen/raBZRvz */
export async function openPaddleOverlayCheckout(params: {
  environment: Environments
  priceId: string
  customData: Record<string, string>
  successUrl: string
}): Promise<void> {
  logCheckout('paddleJs.Checkout.open.call', {
    environment: params.environment,
    priceId: params.priceId,
    customDataKeys: Object.keys(params.customData),
    successUrl: params.successUrl,
  })

  const paddle = await ensurePaddleJs(params.environment)
  if (!paddle) {
    throw new Error('Paddle.js is not configured (set VITE_PADDLE_CLIENT_TOKEN)')
  }

  try {
    paddle.Checkout.open({
      items: [{ priceId: params.priceId, quantity: 1 }],
      customData: params.customData,
      settings: {
        displayMode: 'overlay',
        variant: 'one-page',
        theme: 'light',
        successUrl: params.successUrl,
      },
    })
    logCheckout('paddleJs.Checkout.open.invoked', {
      note: 'If no overlay appears, check browser console for Paddle errors and ad blockers',
    })
  } catch (err: unknown) {
    logCheckout('paddleJs.Checkout.open.throw', {
      error: err instanceof Error ? err.message : String(err),
    })
    throw err
  }
}
